import * as React from "react";
import { type ReactNode } from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import FontAwesomeSolid from "../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import Popup from "./Popup";
import PopupManager, { type PopupId, type PopupType } from "./PopupManager";

type PopupDeletedType = PopupType & {
    deleted?: boolean;
};

interface State {
    popups: Array<PopupDeletedType>;
}

export default class PopupContainer extends React.Component<unknown, State> {
    public state: State = {
        popups: [],
    };

    public componentDidMount(): void {
        PopupManager.addListener(this.handlePopupsChange);
    }

    public componentWillUnmount(): void {
        PopupManager.removeListener(this.handlePopupsChange);
    }

    public handlePopupsChange = (popups: Array<PopupType>): void => {
        // Prepare to check for removed popups
        const oldPopups: Record<PopupId, PopupDeletedType> = {};

        for(let i = 0, l = this.state.popups.length; i < l; i++) {
            oldPopups[this.state.popups[i].id] = {
                ...this.state.popups[i],
                deleted: true,
            };
        }

        // Check for new popups
        const newPopups = [];

        for(let i = 0, l = popups.length; i < l; i++) {
            if(popups[i].local) {
                continue;
            }

            if(typeof oldPopups[popups[i].id] === "undefined") {
                newPopups.push({
                    ...popups[i],
                    deleted: false,
                });
            } else {
                oldPopups[popups[i].id].deleted = false;
            }
        }

        const selected = [
            ...Object.values(oldPopups),
            ...newPopups,
        ];

        this.setState({
            popups: selected,
        });
    };

    public handleHide = (id: PopupId): void => {
        this.setState(prevState => ({
            popups: prevState.popups.filter(popup => popup.id !== id),
        }));
    };

    public render(): React.ReactNode {
        const popups = [];

        for(let i = this.state.popups.length - 1; i >= 0; i--) {
            const popup = this.state.popups[i];

            let icon: ReactNode;

            if(popup.icon) {
                if(typeof popup.icon === "string") {
                    icon = <FontAwesomeSolid icon={popup.icon as IconProp} />;
                } else {
                    icon = popup.icon as ReactNode;
                }
            }

            let message;

            if(typeof popup.message === "string") {
                message = <p>{popup.message}</p>;
            } else {
                message = popup.message;
            }

            popups.push(
                <Popup
                    classes={popup.classes}
                    key={popup.id}
                    id={popup.id}
                    timeout={popup.timeout}
                    remove={!!popup.deleted}
                    onHide={this.handleHide.bind(this, popup.id)}
                >
                    <div className="popup-icon">
                        {icon}
                    </div>
                    <div className="popup-message">
                        {message}
                    </div>
                </Popup>
            );
        }

        return (
            <div id="popups">
                {popups}
            </div>
        );
    }
}
